import { Box, Grid, Typography } from '@mui/material'
import usePractitionerReportInsights from './usePractitionerReportInsights'
import PeriodComparison from './PeriodComparison'
import {
  DonutChart,
  INSIGHT_COLORS,
  InsightsCard,
  InsightsLoading,
  MetricTile,
  RateGroup,
  RatePill,
  SectionEmpty,
  StatTileRow,
  TopKBarChart,
} from './insightsCharts'

function LensHeading({ children }) {
  return (
    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
      {children}
    </Typography>
  )
}

/**
 * Doctor (ROLE_DOCTOR) consultation insights from the practitioner's own PrescriptionData at this
 * clinic location. Organised by three lenses — patient care quality, platform engagement, and the
 * practitioner's own prescribing profile — plus volume and the top clinical entities.
 */
export default function ConsultationInsights({
  active,
  clinicBrandId,
  clinicLocationId,
  from,
  to,
  primaryPeriod,
  comparison,
}) {
  const { data, loading, error } = usePractitionerReportInsights({
    enabled: active,
    reportType: 'prescription',
    clinicBrandId,
    clinicLocationId,
    from,
    to,
  })

  if (loading && !data) return <InsightsLoading />
  if (error && !data) return <SectionEmpty label={error} />
  if (!data) return <SectionEmpty />

  const totals = data.totals || {}
  const rates = data.rates || {}
  const averages = data.averages || {}

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {comparison ? (
        <PeriodComparison
          active={active}
          reportType="prescription"
          clinicBrandId={clinicBrandId}
          clinicLocationId={clinicLocationId}
          primaryPeriod={primaryPeriod}
          primaryData={data}
          comparison={comparison}
        />
      ) : null}

      {/* Volume */}
      <StatTileRow
        tiles={[
          { label: 'Consultations', value: totals.reports, color: INSIGHT_COLORS[0] },
          { label: 'Patients', value: totals.patients, color: INSIGHT_COLORS[6] },
          { label: 'New (in range)', value: totals.new, color: INSIGHT_COLORS[1] },
          { label: 'Repeat (in range)', value: totals.repeat, color: INSIGHT_COLORS[2] },
        ]}
      />

      {/* Lens 1 — patient care quality & continuity */}
      <Box>
        <LensHeading>Patient care</LensHeading>
        <RateGroup
          title="Care quality & continuity"
          subtitle="Share of consultations documenting each element"
          rates={[
            { label: 'Follow-up given', value: rates.followUpGiven },
            { label: 'Diagnosis recorded', value: rates.diagnosisRecorded },
            { label: 'Advice given', value: rates.adviceGiven },
            { label: 'Investigations advised', value: rates.investigationsAdvised },
            { label: 'Observations recorded', value: rates.observationsRecorded },
          ]}
        />
      </Box>

      {/* Lens 2 — platform engagement / downstream bookings */}
      <Box>
        <LensHeading>Platform engagement</LensHeading>
        <RateGroup
          title="Onward care requested"
          subtitle="Consultations that route the patient to more care on the platform"
          rates={[
            { label: 'Therapy requested', value: rates.therapyRequested },
            { label: 'Assessment requested', value: rates.assessmentRequested },
            { label: 'Follow-up booked / dated', value: rates.followUpGiven },
          ]}
        />
      </Box>

      {/* Lens 3 — the practitioner's own prescribing profile */}
      <Box>
        <LensHeading>Prescribing profile</LensHeading>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <InsightsCard title="Prescribing summary">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <MetricTile
                  label="Avg medications / consultation"
                  value={averages.medicationsPerConsultation}
                />
                <RatePill label="Consultations with a prescription" value={rates.medicationsPrescribed} />
              </Box>
            </InsightsCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <InsightsCard title="Medication mix" subtitle="% split across all prescribed drugs">
              <DonutChart items={data.topDrugs} total={totals.drugLines} emptyLabel="No drugs prescribed" />
            </InsightsCard>
          </Grid>
        </Grid>
      </Box>

      {/* Top clinical entities */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Diagnoses">
            <TopKBarChart items={data.topDiagnoses} color={INSIGHT_COLORS[0]} emptyLabel="No diagnoses recorded" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Prescribed Drugs">
            <TopKBarChart items={data.topDrugs} color={INSIGHT_COLORS[1]} emptyLabel="No drugs prescribed" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Complaints">
            <TopKBarChart items={data.topComplaints} color={INSIGHT_COLORS[3]} emptyLabel="No complaints recorded" />
          </InsightsCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InsightsCard title="Top Investigations">
            <TopKBarChart items={data.topInvestigations} color={INSIGHT_COLORS[4]} emptyLabel="No investigations advised" />
          </InsightsCard>
        </Grid>
      </Grid>
    </Box>
  )
}
